/**
 * PointChip primitive (Plan 02-09 / UI-SPEC §"<PointChip>").
 *
 * Three variants (#152: på handoffens toner):
 *   - gain    ok-tonen, "+N" prefix (e.g. "+12 p")
 *   - cost    nøytral tone, no auto-prefix — caller controls the sign
 *             so the same chip can render "-30 p" (transaction list) or
 *             "30 p" (reward cost label). Kostnad er ikke «oppmerksomhet»,
 *             så den er ikke amber.
 *   - default lime-tonen — poeng er en fremheving (saldo-chipen i skissene)
 *
 * Tallet ligger i et eget span med `tnum`, så sifrene ikke hopper.
 *
 * Plan 10 (chore complete sheet) and Plan 11 (wallet) compose with this.
 * Plan 16 (leaderboard rows) uses the default variant directly.
 */
import type { ReactNode } from 'react';
import { TONE_CLASS } from './Pill';

export type PointChipVariant = 'gain' | 'cost' | 'default';

interface PointChipProps {
  points: number;
  variant?: PointChipVariant;
  className?: string;
}

const SURFACE: Record<PointChipVariant, string> = {
  gain: TONE_CLASS.ok,
  cost: TONE_CLASS.neutral,
  default: TONE_CLASS.lime,
};

export function PointChip({
  points,
  variant = 'default',
  className,
}: PointChipProps): ReactNode {
  // Only `gain` injects a sign — cost surfaces just render the value as
  // received (caller passes -30 to show "-30", 30 to show "30").
  const display = variant === 'gain' ? `+${points}` : `${points}`;
  return (
    <span
      data-testid="point-chip"
      className={[
        'inline-flex h-7 items-center gap-0.5 rounded-pill px-2.5 text-xs font-extrabold',
        SURFACE[variant],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span
        data-testid="point-chip-digit"
        className="tnum"
      >
        {display}
      </span>
      <span>p</span>
    </span>
  );
}
